import { convertToHtml, prepend, wrapInner, hasNativeScrolling, wheelDeltaY, addClass } from './utils';
import { Transition, Animation } from './fx';
import { Pane, PaneDimensions, Movable, TapCapture } from './draggable';
import { isFunction } from '../functions';
import UserEvents from '../user-events';
import { off, on } from '../event-utils';
import { deepExtend } from '../deep-extend';
import { Observable } from '../Observable';
const extend = Object.assign, abs = Math.abs, SNAPBACK_DURATION = 500, SCROLLBAR_OPACITY = 0.7, FRICTION = 0.96, VELOCITY_MULTIPLIER = 10, MAX_VELOCITY = 55, OUT_OF_BOUNDS_FRICTION = 0.5, ANIMATED_SCROLLER_PRECISION = 5, 
// SCROLLER_RELEASE_CLASS = 'km-scroller-release',
// SCROLLER_REFRESH_CLASS = 'km-scroller-refresh',
PULL = 'pull', CHANGE = 'change', RESIZE = 'resize', SCROLL = 'scroll', MOUSE_WHEEL_ID = 2;
class ZoomSnapBack extends Animation {
    constructor(options) {
        super();
        extend(this, options);
        this.userEvents.bind('gestureend', this.start.bind(this));
        this.tapCapture.bind('press', this.cancel.bind(this));
    }
    enabled() {
        return this.movable.scale < this.dimensions.minScale;
    }
    done() {
        return this.dimensions.minScale - this.movable.scale < 0.01;
    }
    tick() {
        const movable = this.movable;
        movable.scaleWith(1.1);
        this.dimensions.rescale(movable.scale);
    }
    onEnd() {
        const movable = this.movable;
        movable.scaleTo(this.dimensions.minScale);
        this.dimensions.rescale(movable.scale);
    }
}
class DragInertia extends Animation {
    constructor(options) {
        super();
        extend(this, options, {
            transition: new Transition({
                axis: options.axis,
                movable: options.movable,
                onEnd: () => {
                    this._end();
                }
            })
        });
        this.tapCapture.bind('press', () => {
            this.cancel();
        });
        this.userEvents.bind('end', () => this.start());
        this.userEvents.bind('gestureend', () => this.start());
        this.userEvents.bind('tap', () => this.onEnd());
    }
    onCancel() {
        this.transition.cancel();
    }
    freeze(location) {
        this.cancel();
        this._moveTo(location);
    }
    onEnd() {
        if (this.paneAxis.outOfBounds()) {
            this._snapBack();
        }
        else {
            this._end();
        }
    }
    done() {
        return abs(this.velocity) < 1;
    }
    start(e) {
        let velocity;
        if (!this.dimension.enabled) {
            return;
        }
        if (this.paneAxis.outOfBounds()) {
            if (this.transition._started) {
                this.transition.cancel();
                this.velocity = Math.min(e.touch[this.axis].velocity * this.velocityMultiplier, MAX_VELOCITY);
                super.start();
            }
            else {
                this._snapBack();
            }
        }
        else {
            velocity = e ? (e.touch.id === MOUSE_WHEEL_ID ? 0 : e.touch[this.axis].velocity) : 0;
            this.velocity = Math.max(Math.min(velocity * this.velocityMultiplier, MAX_VELOCITY), -MAX_VELOCITY);
            this.tapCapture.captureNext();
            super.start();
        }
    }
    tick() {
        const dimension = this.dimension, friction = this.paneAxis.outOfBounds() ? OUT_OF_BOUNDS_FRICTION : this.friction, delta = this.velocity *= friction;
        let location = this.movable[this.axis] + delta;
        if (!this.elastic && dimension.outOfBounds(location)) {
            location = Math.max(Math.min(location, dimension.max), dimension.min);
            this.velocity = 0;
        }
        this.movable.moveAxis(this.axis, location);
    }
    _end() {
        this.tapCapture.cancelCapture();
        this.end();
    }
    end() { }
    _snapBack() {
        const dimension = this.dimension, snapBack = this.movable[this.axis] > dimension.max ? dimension.max : dimension.min;
        this._moveTo(snapBack);
    }
    _moveTo(location) {
        this.transition.moveTo({
            location: location,
            duration: SNAPBACK_DURATION,
            ease: (...args) => {
                Transition.easeOutExpo.apply(null, args);
            }
        });
    }
}
class AnimatedScroller extends Animation {
    constructor(options) {
        super();
        extend(this, options, {
            origin: {},
            destination: {},
            offset: {}
        });
    }
    moveTo(_to) { }
    tick() {
        this._updateCoordinates();
        this.moveTo(this.origin);
    }
    done() {
        return abs(this.offset.y) < ANIMATED_SCROLLER_PRECISION && abs(this.offset.x) < ANIMATED_SCROLLER_PRECISION;
    }
    onEnd() {
        this.moveTo(this.destination);
        if (this.callback) {
            this.callback.call();
        }
    }
    setCoordinates(from, to) {
        this.offset = {};
        this.origin = from;
        this.destination = to;
    }
    setCallback(callback) {
        if (callback && isFunction(callback)) {
            this.callback = callback;
        }
        else {
            callback = undefined;
        }
    }
    _updateCoordinates() {
        this.offset = {
            x: (this.destination.x - this.origin.x) / 4,
            y: (this.destination.y - this.origin.y) / 4
        };
        this.origin = {
            y: this.origin.y + this.offset.y,
            x: this.origin.x + this.offset.x
        };
    }
}
class ScrollBar {
    constructor(options) {
        const horizontal = options.axis === 'x';
        const orientation = (horizontal ? 'horizontal' : 'vertical');
        const element = convertToHtml('<div class="km-touch-scrollbar km-' + orientation + '-scrollbar" />');
        extend(this, options, {
            element: element,
            elementSize: 0,
            movable: new Movable(element),
            scrollMovable: options.movable,
            alwaysVisible: options.alwaysVisible,
            size: horizontal ? 'width' : 'height'
        });
        this.scrollMovable.bind(CHANGE, this.refresh.bind(this));
        this.container.appendChild(element);
        if (options.alwaysVisible) {
            this.show();
        }
    }
    refresh() {
        const axis = this.axis, dimension = this.dimension, paneSize = dimension.size, scrollMovable = this.scrollMovable, sizeRatio = paneSize / dimension.total;
        let size = Math.round(paneSize * sizeRatio), position = Math.round(-scrollMovable[axis] * sizeRatio);
        if (sizeRatio >= 1) {
            this.element.style.display = 'none';
        }
        else {
            this.element.style.display = '';
        }
        if (position + size > paneSize) {
            size = paneSize - position;
        }
        else if (position < 0) {
            size += position;
            position = 0;
        }
        if (this.elementSize !== size) {
            this.element.style[this.size] = size + 'px';
            this.elementSize = size;
        }
        this.movable.moveAxis(axis, position);
    }
    show() {
        this.element.style.opacity = SCROLLBAR_OPACITY;
        this.element.style.visibility = 'visible';
    }
    hide() {
        if (!this.alwaysVisible) {
            this.element.style.opacity = 0;
        }
    }
}
const defaultScrollerOptions = {
    name: 'Scroller',
    zoom: false,
    pullOffset: 140,
    visibleScrollHints: false,
    elastic: true,
    useNative: false,
    mousewheelScrolling: true,
    avoidScrolling: () => false,
    pullToRefresh: false,
    messages: {
        pullTemplate: 'Pull to refresh',
        releaseTemplate: 'Release to refresh',
        refreshTemplate: 'Refreshing'
    }
};
// export class Scroller extends Class {
export class Scroller extends Observable {
    constructor(element, options) {
        super();
        this.element = element = element[0] || element;
        this._initOptions(options);
        this.events.push(PULL, SCROLL, RESIZE);
        const hasScrolling = hasNativeScrolling();
        this._native = this.options.useNative && hasScrolling;
        const scrollHeader = convertToHtml('<div class="km-scroll-header"/>');
        if (this._native) {
            addClass(element, 'km-native-scroller');
            prepend(scrollHeader, element);
            extend(this, {
                scrollElement: element,
                fixedContainer: element.children[0]
            });
            return;
        }
        element.style.overflow = 'hidden';
        addClass(element, 'km-scroll-wrapper');
        const scrollContainer = convertToHtml('<div class="km-scroll-container"/>');
        wrapInner(element, scrollContainer);
        prepend(scrollHeader, element);
        const inner = element.children[1], tapCapture = new TapCapture(element), movable = new Movable(inner), dimensions = new PaneDimensions({
            element: inner,
            container: element,
            forcedEnabled: this.options.zoom
        }), avoidScrolling = this.options.avoidScrolling, userEvents = new UserEvents(element, {
            touchAction: 'none',
            allowSelection: true,
            preventDragEvent: true,
            captureUpIfMoved: true,
            multiTouch: this.options.zoom,
            supportDoubleTap: this.options.supportDoubleTap,
            start: (e) => {
                dimensions.refresh();
                const velocityX = abs(e.x.velocity), velocityY = abs(e.y.velocity), horizontalSwipe = velocityX * 2 >= velocityY, originatedFromFixedContainer = this.fixedContainer.contains(e.event.target), verticalSwipe = velocityY * 2 >= velocityX;
                if (!originatedFromFixedContainer && !avoidScrolling(e) && this.enabled && (dimensions.x.enabled && horizontalSwipe || dimensions.y.enabled && verticalSwipe)) {
                    userEvents.capture();
                }
                else {
                    userEvents.cancel();
                }
            }
        }), pane = new Pane({
            movable: movable,
            dimensions: dimensions,
            userEvents: userEvents,
            elastic: this.options.elastic
        }), zoomSnapBack = new ZoomSnapBack({
            movable: movable,
            dimensions: dimensions,
            userEvents: userEvents,
            tapCapture: tapCapture
        }), animatedScroller = new AnimatedScroller({
            moveTo: (coordinates) => {
                this.scrollTo(coordinates.x, coordinates.y);
            }
        });
        movable.bind(CHANGE, () => {
            this.scrollTop = -movable.y;
            this.scrollLeft = -movable.x;
            this.trigger(SCROLL, {
                scrollTop: this.scrollTop,
                scrollLeft: this.scrollLeft
            });
        });
        if (this.options.mousewheelScrolling) {
            this._wheelScrollHandler = this._wheelScroll.bind(this);
            on(element, 'wheel', this._wheelScrollHandler);
        }
        extend(this, {
            movable: movable,
            dimensions: dimensions,
            zoomSnapBack: zoomSnapBack,
            animatedScroller: animatedScroller,
            userEvents: userEvents,
            pane: pane,
            tapCapture: tapCapture,
            pulled: false,
            enabled: true,
            scrollElement: inner,
            scrollTop: 0,
            scrollLeft: 0,
            fixedContainer: element.children[0]
        });
        this._initAxis('x');
        this._initAxis('y');
        this._wheelEnd = () => {
            this._wheel = false;
            this.userEvents.end(0, this._wheelY);
        };
        dimensions.refresh();
        if (this.options.pullToRefresh) {
            this._initPullToRefresh();
        }
        this.bind(this.events, this.options);
    }
    _initOptions(options) {
        this.options = deepExtend({}, this.options, defaultScrollerOptions, options);
    }
    _wheelScroll(e) {
        if (e.ctrlKey) {
            return;
        }
        if (!this._wheel) {
            this._wheel = true;
            this._wheelY = 0;
            this.userEvents.press(0, this._wheelY);
        }
        clearTimeout(this._wheelTimeout);
        this._wheelTimeout = setTimeout(() => this._wheelEnd(), 50);
        const delta = wheelDeltaY(e);
        if (delta) {
            this._wheelY += delta;
            this.userEvents.move(0, this._wheelY);
        }
        e.preventDefault();
    }
    makeVirtual() {
        this.dimensions.y.makeVirtual();
    }
    virtualSize(min, max) {
        this.dimensions.y.virtualSize(min, max);
    }
    height() {
        return this.dimensions.y.size;
    }
    scrollHeight() {
        return this.scrollElement.scrollHeight;
    }
    scrollWidth() {
        return this.scrollElement.scrollWidth;
    }
    _resize() {
        if (!this._native) {
            this.contentResized();
        }
    }
    setOptions(options) {
        this._initOptions(options);
        if (options.pullToRefresh) {
            this._initPullToRefresh();
        }
    }
    reset() {
        if (this._native) {
            this.scrollElement.scrollTop(0);
        }
        else {
            this.movable.moveTo({
                x: 0,
                y: 0
            });
            this._scale(1);
        }
    }
    contentResized() {
        this.dimensions.refresh();
        if (this.pane.x.outOfBounds()) {
            this.movable.moveAxis('x', this.dimensions.x.min);
        }
        if (this.pane.y.outOfBounds()) {
            this.movable.moveAxis('y', this.dimensions.y.min);
        }
    }
    zoomOut() {
        const dimensions = this.dimensions;
        dimensions.refresh();
        this._scale(dimensions.fitScale);
        this.movable.moveTo(dimensions.centerCoordinates());
    }
    enable() {
        this.enabled = true;
    }
    disable() {
        this.enabled = false;
    }
    scrollTo(x, y) {
        if (this._native) {
            this.scrollElement.scrollLeft(abs(x));
            this.scrollElement.scrollTop(abs(y));
        }
        else {
            this.dimensions.refresh();
            this.movable.moveTo({
                x: x,
                y: y
            });
        }
    }
    animatedScrollTo(x, y, callback) {
        let from, to;
        if (this._native) {
            this.scrollTo(x, y);
        }
        else {
            from = {
                x: this.movable.x,
                y: this.movable.y
            };
            to = {
                x: x,
                y: y
            };
            this.animatedScroller.setCoordinates(from, to);
            this.animatedScroller.setCallback(callback);
            this.animatedScroller.start();
        }
    }
    // kept for API compatibility, not used
    pullHandled() {
        // let this = this;
        // removeClass(this.refreshHint, SCROLLER_REFRESH_CLASS);
        // this.hintContainer.innerHTML = this.pullTemplate({}));
        // this.yinertia.onEnd();
        // this.xinertia.onEnd();
        // this.userEvents.cancel();
    }
    destroy() {
        const element = this.element;
        off(element, 'wheel', this._wheelScrollHandler);
        if (this.userEvents) {
            this.userEvents.destroy();
        }
        if (this.tapCapture) {
            this.tapCapture.destroy();
        }
    }
    _scale(scale) {
        this.dimensions.rescale(scale);
        this.movable.scaleTo(scale);
    }
    _initPullToRefresh() {
    }
    // kept for API compatibility, not used
    _dragEnd() {
        // let this = this;
        // if (!this.pulled) {
        //     return;
        // }
        // this.pulled = false;
        // removeClass(this.refreshHint, SCROLLER_RELEASE_CLASS);
        // addClass(this.refreshHint, SCROLLER_REFRESH_CLASS);
        // this.hintContainer.innerHTML = this.refreshTemplate({});
        // this.yinertia.freeze(this.options.pullOffset / 2);
        // this.trigger('pull');
    }
    // kept for API compatibility, not used
    _paneChange() {
        // let this = this;
        // if (this.movable.y / OUT_OF_BOUNDS_FRICTION > this.options.pullOffset) {
        //     if (!this.pulled) {
        //         this.pulled = true;
        //         this.refreshHint.removeClass(SCROLLER_REFRESH_CLASS).addClass(SCROLLER_RELEASE_CLASS);
        //         this.hintContainer.html(this.releaseTemplate({}));
        //         this.hintContainer.html(this.releaseTemplate({}));
        //     }
        // } else if (this.pulled) {
        //     this.pulled = false;
        //     this.refreshHint.removeClass(SCROLLER_RELEASE_CLASS);
        //     this.hintContainer.html(this.pullTemplate({}));
        // }
    }
    _initAxis(axis) {
        const movable = this.movable, dimension = this.dimensions[axis], tapCapture = this.tapCapture, paneAxis = this.pane[axis], scrollBar = new ScrollBar({
            axis: axis,
            movable: movable,
            dimension: dimension,
            container: this.element,
            alwaysVisible: this.options.visibleScrollHints
        });
        dimension.bind(CHANGE, () => {
            scrollBar.refresh();
        });
        paneAxis.bind(CHANGE, () => {
            scrollBar.show();
        });
        this[axis + 'inertia'] = new DragInertia({
            axis: axis,
            paneAxis: paneAxis,
            movable: movable,
            tapCapture: tapCapture,
            userEvents: this.userEvents,
            dimension: dimension,
            elastic: this.options.elastic,
            friction: this.options.friction || FRICTION,
            velocityMultiplier: this.options.velocityMultiplier || VELOCITY_MULTIPLIER,
            end: () => {
                scrollBar.hide();
                this.trigger('scrollEnd', {
                    axis: axis,
                    scrollTop: this.scrollTop,
                    scrollLeft: this.scrollLeft
                });
            }
        });
    }
}
